import { useEffect, useRef } from 'react';

import { useEvent } from 'react-use';

import {
  MESSAGE_TYPES,
  isParentPointerMoveMessage,
  isParentPointerUpMessage,
} from '@/lib/constants/post-message-types';
import { isInIframe } from '@/lib/utils/dom';

/**
 * Enables smooth carousel drag across cross-origin iframe boundaries.
 * Only active inside iframes — in standalone mode, all handlers are no-ops.
 *
 * Problem: When a pointer exits a cross-origin iframe, the browser fires
 * pointerleave/pointerout/pointercancel on document. Swiper registers all
 * of these as onTouchEnd triggers, prematurely ending the drag. Additionally,
 * the iframe stops receiving pointermove events, so the drag freezes.
 *
 * Solution (two parts):
 * 1. Block Swiper's drag-end events during active drag via capture-phase
 *    listeners on window.
 * 2. Notify the parent page of drag start/end. The parent forwards
 *    pointermove (with iframe-relative coords) and pointerup via postMessage.
 *    This hook dispatches them as synthetic pointer events so Swiper
 *    continues tracking seamlessly.
 *
 * CAROUSEL_DRAG_START is sent only when the pointer actually leaves the
 * iframe (pointerleave on document), not on movement thresholds or timers.
 * This ensures simple clicks/taps never trigger the parent drag proxy and
 * avoids iframe focus loss.
 */
export function useParentPointerUp(el: HTMLElement | null | undefined) {
  const activePointerIdRef = useRef<number | null>(null);
  const dragStartedRef = useRef(false);

  const resetDrag = () => {
    if (dragStartedRef.current) {
      window.parent?.postMessage({ type: MESSAGE_TYPES.CAROUSEL_DRAG_END }, '*');
    }
    activePointerIdRef.current = null;
    dragStartedRef.current = false;
  };

  const endDrag = () => {
    if (activePointerIdRef.current === null) return;
    const pointerId = activePointerIdRef.current;
    resetDrag();
    el?.dispatchEvent(new PointerEvent('pointerup', { bubbles: true, pointerId }));
  };

  useEvent(
    'pointerdown',
    (e: Event) => {
      if (!isInIframe()) return;
      activePointerIdRef.current = (e as PointerEvent).pointerId;
      dragStartedRef.current = false;
    },
    el ?? null,
  );

  useEvent('pointerup', resetDrag, typeof document !== 'undefined' ? document : null);

  // Capture-phase listeners on window intercept events before Swiper's
  // at-target listeners on document.
  //
  // pointerleave on document = pointer exited the iframe. This is the
  // trigger for CAROUSEL_DRAG_START — the only moment we need the parent
  // drag proxy. Other events (pointercancel, pointerout) are only blocked
  // during a confirmed drag to avoid interfering with normal clicks.
  useEffect(() => {
    if (!el || !isInIframe()) return;

    const blockDragEnd = (e: Event) => {
      if (e.type === 'pointerleave' && e.target === document) {
        if (activePointerIdRef.current === null) return;
        e.stopPropagation();
        if (!dragStartedRef.current) {
          dragStartedRef.current = true;
          window.parent?.postMessage({ type: MESSAGE_TYPES.CAROUSEL_DRAG_START }, '*');
        }
        return;
      }

      if (!dragStartedRef.current) return;

      if (e.type === 'pointercancel') {
        e.stopPropagation();
        return;
      }
      if (e.type === 'pointerout' && el.contains(e.target as Node)) {
        e.stopPropagation();
      }
    };

    window.addEventListener('pointerleave', blockDragEnd, true);
    window.addEventListener('pointercancel', blockDragEnd, true);
    window.addEventListener('pointerout', blockDragEnd, true);

    return () => {
      window.removeEventListener('pointerleave', blockDragEnd, true);
      window.removeEventListener('pointercancel', blockDragEnd, true);
      window.removeEventListener('pointerout', blockDragEnd, true);
    };
  }, [el]);

  useEvent('message', (e: Event) => {
    if (activePointerIdRef.current === null) return;
    const msgEvent = e as MessageEvent;

    if (isParentPointerMoveMessage(msgEvent.data)) {
      el?.dispatchEvent(
        new PointerEvent('pointermove', {
          bubbles: true,
          buttons: 1,
          pointerId: activePointerIdRef.current,
          clientX: msgEvent.data.clientX,
          clientY: msgEvent.data.clientY,
        }),
      );
      return;
    }

    if (isParentPointerUpMessage(msgEvent.data)) {
      endDrag();
    }
  });

  useEvent('blur', () => {
    if (!dragStartedRef.current) return;
    endDrag();
  });
}
